import React, { useCallback } from "react";
import { Box, Tabs, Tab, useMediaQuery, useTheme } from "@mui/material";
import useTouchInteraction from "../../hooks/useTouchInteraction";

/**
 * Shared minimalist tab bar used across the page-level tabbed views
 * (Orders, Funds, Portfolio, Trade History, Price Alerts, ...).
 *
 * - blends into the page background (no white card), only a bottom divider
 * - compact & left-aligned on desktop, full-width for touch on mobile
 * - clean sliding primary underline indicator
 *
 * Props:
 *   value     current selected tab value (number | string)
 *   onChange  (event, newValue) => void
 *   tabs      [{ label, value }]
 *   sticky    keep the bar pinned to top on mobile (default false)
 *   sx        extra styles for the container
 */
const PageTab = React.memo(({ label, value, selected, onChange, stretch }) => {
  const handleClick = useCallback(
    (e) => {
      if (!e) return;
      onChange(e, value);
    },
    [onChange, value]
  );

  const { handlers, touchStates } = useTouchInteraction(handleClick, true);

  return (
    <Tab
      {...handlers}
      label={label}
      value={value}
      onClick={(e) => handleClick(e)}
      disableRipple
      sx={{
        minHeight: "36px",
        height: "36px",
        textTransform: "none",
        fontWeight: selected ? 600 : 500,
        whiteSpace: "nowrap",
        fontSize: "0.875rem",
        flex: stretch ? 1 : "none",
        minWidth: "auto",
        px: 1.5,
        opacity: 1,
        color: selected ? "primary.main" : "text.secondary",
        transition: "color 0.2s ease, background-color 0.2s ease",
        backgroundColor:
          touchStates.isActive && !touchStates.isTouchMoved ? "action.hover" : "transparent",
        WebkitTapHighlightColor: "transparent",
        "&:hover": { color: "text.primary" },
        "&:active": {
          backgroundColor: !touchStates.isTouchMoved ? "action.hover" : "transparent",
        },
      }}
    />
  );
});

const PageTabs = ({ value, onChange, tabs = [], sticky = false, action, sx }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // On mobile: many tabs (>3) scroll so they don't collide; a single tab stays
  // compact/left-aligned (fullWidth would stretch it across the bar and look
  // "elongated"); 2–3 tabs share the width.
  const mobileVariant = tabs.length > 3 ? "scrollable" : tabs.length <= 1 ? "standard" : "fullWidth";
  // Tabs only stretch to fill the row in fullWidth (mobile, 2–3 tabs); otherwise
  // they stay snug to their label.
  const stretch = isMobile && mobileVariant === "fullWidth";

  return (
    <Box
      sx={{
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: "background.default",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        pr: action ? 1 : 0,
        ...(sticky && {
          [theme.breakpoints.down("sm")]: {
            position: "sticky",
            top: 0,
            zIndex: 1200,
            backgroundColor: "background.default",
          },
        }),
        ...sx,
      }}
    >
      <Tabs
        value={value}
        onChange={onChange}
        variant={isMobile ? mobileVariant : "standard"}
        scrollButtons={false}
        allowScrollButtonsMobile={false}
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 36,
          "& .MuiTabs-flexContainer": { height: 36 },
          "& .MuiTabs-scroller": {
            // hide the scrollbar but keep the row swipeable on mobile
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          },
          "& .MuiTabs-indicator": {
            height: 2,
            borderRadius: 1,
            backgroundColor: "primary.main",
          },
        }}
      >
        {tabs.map((t) => (
          <PageTab
            key={t.value}
            label={t.label}
            value={t.value}
            selected={value === t.value}
            onChange={onChange}
            stretch={stretch}
          />
        ))}
      </Tabs>
      {action ? (
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>{action}</Box>
      ) : null}
    </Box>
  );
};

export default PageTabs;
